export function withCompactAccordionMode(originalHook) {
  return {
    mounted() {
      originalHook.mounted.call(this)
      this._accordionResponsiveMode = null
      this._accordionCompactState = new Map()
      this._installAccordionResizeObserver()
      this._syncAccordionResponsiveMode()
    },

    updated() {
      originalHook.updated.call(this)

      // Fluxon has just reconciled the server-rendered source of truth. Treat
      // that state as fresh even when the panel is currently forced open at xl.
      this._accordionResponsiveMode = null
      this._syncAccordionResponsiveMode()
    },

    destroyed() {
      this._accordionResizeObserver?.disconnect()
      window.removeEventListener("resize", this._accordionResizeHandler)
      originalHook.destroyed?.call(this)
    },

    _installAccordionResizeObserver() {
      if (this.el.dataset.collapseBelow !== "xl") return

      this._accordionResizeObserver = new ResizeObserver(() =>
        this._syncAccordionResponsiveMode(),
      )
      this._accordionResizeObserver.observe(this.el)

      // `display: contents` is a valid composition for grid-based consumers,
      // but it has no box for ResizeObserver to measure. The window listener
      // covers those consumers while the observer handles container changes.
      this._accordionResizeHandler = () => this._syncAccordionResponsiveMode()
      window.addEventListener("resize", this._accordionResizeHandler)
    },

    _accordionItems() {
      return Array.from(this.el.children).filter(
        (child) => child.dataset.part === "item",
      )
    },

    _accordionPart(item, part) {
      return Array.from(item.children).find(
        (child) => child.dataset.part === part,
      )
    },

    _isAccordionDesktop() {
      const trigger = this._accordionItems()
        .map((item) => this._accordionPart(item, "trigger"))
        .find(Boolean)

      return trigger && getComputedStyle(trigger).display === "none"
    },

    _captureAccordionCompactState() {
      this._accordionCompactState = new Map(
        this._accordionItems().map((item) => [
          item.id,
          item.hasAttribute("data-expanded"),
        ]),
      )
    },

    _applyAccordionItemState(item, expanded) {
      const trigger = this._accordionPart(item, "trigger")
      const panel = this._accordionPart(item, "panel")

      item.toggleAttribute("data-expanded", expanded)
      trigger?.setAttribute("aria-expanded", String(expanded))
      panel?.setAttribute("aria-hidden", String(!expanded))
      panel?.toggleAttribute("inert", !expanded)
    },

    _exposeAccordionForDesktop() {
      this._accordionItems().forEach((item) =>
        this._applyAccordionItemState(item, true),
      )
    },

    _restoreAccordionCompactState() {
      this._accordionItems().forEach((item) =>
        this._applyAccordionItemState(
          item,
          this._accordionCompactState.get(item.id) ?? false,
        ),
      )
    },

    _syncAccordionResponsiveMode() {
      if (this.el.dataset.collapseBelow !== "xl") return

      if (this._isAccordionDesktop()) {
        if (this._accordionResponsiveMode !== "desktop") {
          this._captureAccordionCompactState()
        }

        this._exposeAccordionForDesktop()
        this._accordionResponsiveMode = "desktop"
      } else {
        if (this._accordionResponsiveMode === "desktop") {
          this._restoreAccordionCompactState()
        }

        this._accordionResponsiveMode = "compact"
      }
    },
  }
}
